import { parseLeaveCalendarDate, toLeaveCalendarKey } from '../../../components/shared/leaveCalendarDateUtils'

export const HRM_STORAGE_KEYS = {
  leaves: 'provio_hrm_leaves',
  onboarding: 'provio_hrm_onboarding',
  holidays: 'provio_hrm_holidays',
}

export const HRM_LEAVE_STATUSES = ['Pending Approval', 'Approved', 'Rejected'/*, 'Cancelled'*/]

export const HRM_STANDARD_LEAVE_TYPES = ['PTO', 'Sick Leave']
export const HRM_LEAVE_TYPES = ['PTO', 'Sick Leave', 'Comp Off']

export const HRM_EMPLOYEE_COUNTRIES = ['India', 'USA']

export const HRM_LEAVE_ENTITLEMENTS = {
  India: 15,
  USA: 15,
}

export const HRM_ONBOARDING_DOCUMENTS = [
  { key: 'medicalLicense', label: 'Medical License', expiryRequired: true },
  { key: 'driversLicense', label: "Driver's License", expiryRequired: true },
  { key: 'dea', label: 'DEA', expiryRequired: true },
  { key: 'resumeCv', label: 'Resume/CV', expiryRequired: false },
  { key: 'collaborativeAgreement', label: 'Collaborative Agreement', expiryRequired: true },
  { key: 'npdb', label: 'NPDB', expiryRequired: false },
  { key: 'degreeCertificate', label: 'Degree Certificate', expiryRequired: false },
  { key: 'aanpAancCertificate', label: 'AANP/AANC Certificate', expiryRequired: true },
  { key: 'malpractice', label: 'Malpractice', expiryRequired: true },
  { key: 'covidCertificate', label: 'Covid 19 Vaccine Certificate/Exemption Letter', expiryRequired: false },
  { key: 'fluTest', label: 'FLU Test Copy Within One Year', expiryRequired: true },
  { key: 'tbTest', label: 'TB Test Copy Within Last 6 Months', expiryRequired: true },
  { key: 'passportPhoto', label: 'Passport Photo', expiryRequired: false },
]

const safeParse = (value, fallback) => {
  if (!value) return fallback
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

const normalizeHRMCountry = (country = 'India') => {
  const countryName = String(country || 'India').trim().toLowerCase()
  if (countryName.includes('usa') || countryName.includes('united states') || countryName === 'us') {
    return 'USA'
  }
  return 'India'
}

const getHolidayStorageKey = (country = 'India', year = new Date().getFullYear()) => {
  const normalizedCountry = normalizeHRMCountry(country).toLowerCase()
  const selectedYear = Number(year) || new Date().getFullYear()
  return `${HRM_STORAGE_KEYS.holidays}_${normalizedCountry}_${selectedYear}`
}

const withCurrentTimestamp = (record) => ({
  ...record,
  updatedAt: new Date().toISOString(),
  createdAt: record.createdAt || new Date().toISOString(),
})

const formatDateKey = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const parseHRMDate = (value) => {
  return parseLeaveCalendarDate(value)
}

const makeDate = (year, monthIndex, day) => new Date(year, monthIndex, day)

const nthWeekdayOfMonth = (year, monthIndex, weekday, occurrence) => {
  const date = new Date(year, monthIndex, 1)
  const firstWeekday = date.getDay()
  const offset = (weekday - firstWeekday + 7) % 7
  const day = 1 + offset + (occurrence - 1) * 7
  return makeDate(year, monthIndex, day)
}

const lastWeekdayOfMonth = (year, monthIndex, weekday) => {
  const date = new Date(year, monthIndex + 1, 0)
  const lastWeekday = date.getDay()
  const offset = (lastWeekday - weekday + 7) % 7
  return makeDate(year, monthIndex, date.getDate() - offset)
}

const observeFixedHoliday = (year, monthIndex, day) => {
  const date = makeDate(year, monthIndex, day)
  const dayOfWeek = date.getDay()
  if (dayOfWeek === 6) return makeDate(year, monthIndex, day - 1)
  if (dayOfWeek === 0) return makeDate(year, monthIndex, day + 1)
  return date
}

const buildUsaHolidayCalendar = (year) => [
  { date: formatDateKey(observeFixedHoliday(year, 0, 1)), label: 'New Year\'s Day' },
  { date: formatDateKey(nthWeekdayOfMonth(year, 0, 1, 3)), label: 'Martin Luther King Jr. Day' },
  { date: formatDateKey(nthWeekdayOfMonth(year, 1, 1, 3)), label: 'Washington\'s Birthday' },
  { date: formatDateKey(lastWeekdayOfMonth(year, 4, 1)), label: 'Memorial Day' },
  { date: formatDateKey(observeFixedHoliday(year, 5, 19)), label: 'Juneteenth National Independence Day' },
  { date: formatDateKey(observeFixedHoliday(year, 6, 4)), label: 'Independence Day' },
  { date: formatDateKey(nthWeekdayOfMonth(year, 8, 1, 1)), label: 'Labor Day' },
  { date: formatDateKey(nthWeekdayOfMonth(year, 9, 1, 2)), label: 'Columbus Day' },
  { date: formatDateKey(observeFixedHoliday(year, 10, 11)), label: 'Veterans Day' },
  { date: formatDateKey(nthWeekdayOfMonth(year, 10, 4, 4)), label: 'Thanksgiving Day' },
  { date: formatDateKey(observeFixedHoliday(year, 11, 25)), label: 'Christmas Day' },
]

const buildIndiaHolidayCalendar = (year) => [
  { date: `${year}-01-01`, label: 'New Year\'s Day' },
  { date: `${year}-01-15`, label: 'Pongal / Makar Sankranti' },
  { date: `${year}-01-26`, label: 'Republic Day' },
  { date: `${year}-03-21`, label: 'Id-Ul-Fitr (Ramzan)' },
  { date: `${year}-03-31`, label: 'Mahavir Jayanti' },
  { date: `${year}-04-03`, label: 'Good Friday' },
  { date: `${year}-05-01`, label: 'Buddha Purnima' },
  { date: `${year}-05-28`, label: 'Id-Ul-Zuha (Bakrid)' },
  { date: `${year}-06-26`, label: 'Muharram' },
  { date: `${year}-08-15`, label: 'Independence Day' },
  { date: `${year}-08-26`, label: 'Prophet Mohammed\'s Birthday (Id-E-Milad)' },
  { date: `${year}-09-14`, label: 'Ganesh Chaturthi / Vinayak Chaturthi' },
  { date: `${year}-10-02`, label: 'Mahatma Gandhi\'s Birthday' },
  { date: `${year}-10-19`, label: 'Dussehra (Vijaya Dasami)' },
  { date: `${year}-11-08`, label: 'Diwali (Deepavali)' },
  { date: `${year}-11-24`, label: 'Guru Nanak\'s Birthday' },
  { date: `${year}-12-24`, label: 'Christmas Eve' },
  { date: `${year}-12-25`, label: 'Christmas Day' },
]

const buildHolidayRecord = (country, year, item, index = 0) => {
  const normalizedCountry = normalizeHRMCountry(country)
  const selectedYear = Number(year) || new Date().getFullYear()
  const date = String(item?.date || '').slice(0, 10)
  const label = String(item?.label || '').trim()
  const key = `${normalizedCountry.toLowerCase()}-${selectedYear}-${date || index}`

  return {
    id: item?.id || key,
    key,
    date,
    label,
    country: normalizedCountry,
    year: selectedYear,
  }
}

const buildHolidayRecords = (country, year) => {
  const normalizedCountry = normalizeHRMCountry(country)
  const selectedYear = Number(year) || new Date().getFullYear()
  const source = normalizedCountry === 'USA' ? buildUsaHolidayCalendar(selectedYear) : buildIndiaHolidayCalendar(selectedYear)
  return source.map((item, index) => buildHolidayRecord(normalizedCountry, selectedYear, item, index))
}

const normalizeHolidayRecords = (records = [], country, year) => {
  const normalizedCountry = normalizeHRMCountry(country)
  const selectedYear = Number(year) || new Date().getFullYear()

  return (Array.isArray(records) ? records : [])
    .map((record, index) => buildHolidayRecord(normalizedCountry, selectedYear, record, index))
    .filter((record) => record.date && record.label)
    .sort((a, b) => a.date.localeCompare(b.date) || a.label.localeCompare(b.label))
}

export const getHRMHolidayCalendar = (country = 'India', year = new Date().getFullYear()) => {
  const normalizedCountry = normalizeHRMCountry(country)
  const selectedYear = Number(year) || new Date().getFullYear()
  const storageKey = getHolidayStorageKey(normalizedCountry, selectedYear)
  const fallbackFactory = () => buildHolidayRecords(normalizedCountry, selectedYear)
  const records = readHRMRecords(storageKey, fallbackFactory)
  return normalizeHolidayRecords(records, normalizedCountry, selectedYear)
}

export const saveHRMHolidayCalendar = (country = 'India', year = new Date().getFullYear(), records = []) => {
  const normalizedCountry = normalizeHRMCountry(country)
  const selectedYear = Number(year) || new Date().getFullYear()
  const storageKey = getHolidayStorageKey(normalizedCountry, selectedYear)
  const nextRecords = normalizeHolidayRecords(records, normalizedCountry, selectedYear)
  writeHRMRecords(storageKey, nextRecords)
  return nextRecords
}

export const resetHRMHolidayCalendar = (country = 'India', year = new Date().getFullYear()) => {
  return saveHRMHolidayCalendar(country, year, buildHolidayRecords(country, year))
}

export const getHRMHolidayStorageKey = (country = 'India', year = new Date().getFullYear()) => getHolidayStorageKey(country, year)

export const getHRMHolidayDefaults = (country = 'India', year = new Date().getFullYear()) => buildHolidayRecords(country, year)

export const normalizeHRMHolidayCountry = normalizeHRMCountry

export const normalizeHRMHolidayRecords = (records = [], country = 'India', year = new Date().getFullYear()) =>
  normalizeHolidayRecords(records, country, year)

export const isHRMWeekend = (dateValue) => {
  const date = parseHRMDate(dateValue)
  if (!date) return false
  const day = date.getDay()
  return day === 0 || day === 6
}

export const calculateHRMWorkingDays = (fromDate, toDate, holidayDates = []) => {
  if (!fromDate || !toDate) return 0
  const start = parseHRMDate(fromDate)
  const end = parseHRMDate(toDate)
  if (!start || !end || end < start) return 0

  const holidaySet = new Set(
    holidayDates.map((date) => toLeaveCalendarKey(date)).filter(Boolean)
  )
  let total = 0
  const cursor = new Date(start)

  while (cursor <= end) {
    const key = formatDateKey(cursor)
    if (!isHRMWeekend(cursor) && !holidaySet.has(key)) {
      total += 1
    }
    cursor.setDate(cursor.getDate() + 1)
  }

  return total
}

const getCompOffDateCount = (leave = {}) => {
  const rawCompDates =
    leave?.compensationDate ??
    leave?.compOffDates ??
    leave?.CompOffDates ??
    leave?.CompensationDate ??
    ''

  if (!rawCompDates) return 0

  const normalizedDates = (Array.isArray(rawCompDates) ? rawCompDates : String(rawCompDates).split(','))
    .map((dateValue) => toLeaveCalendarKey(String(dateValue || '').trim()))
    .filter(Boolean)

  return new Set(normalizedDates).size
}

export const getHRMEffectiveLeaveDays = (leave = {}, holidayDates = []) => {
  const approvedLeaveDays = Number(calculateHRMWorkingDays(leave?.fromDate, leave?.toDate, holidayDates) || 0)
  const compOffDateCount = getCompOffDateCount(leave)
  return Math.max(approvedLeaveDays - compOffDateCount, 0)
}

export const getHRMLeaveBalance = ({
  leaves = [],
  employeeName = '',
  employeeCountry = 'India',
  year = new Date().getFullYear(),
  entitlement,
  excludeRecordId = null,
} = {}) => {
  const entitlementDays = Number(entitlement ?? HRM_LEAVE_ENTITLEMENTS[employeeCountry] ?? 15)
  const countryName = String(employeeCountry || 'India').toLowerCase()
  const employeeNameKey = String(employeeName || '').trim().toLowerCase()
  const holidayDates = getHRMHolidayCalendar(employeeCountry, year).map((item) => item.date)

  const usedDays = leaves.reduce((sum, leave) => {
    if (!leave) return sum
    if (excludeRecordId && String(leave.id) === String(excludeRecordId)) return sum
    if (employeeNameKey && String(leave.employeeName || '').trim().toLowerCase() !== employeeNameKey) return sum
    if (countryName && String(leave.employeeCountry || '').toLowerCase() !== countryName) return sum
    if (leave.status !== 'Approved') return sum

    const days = getHRMEffectiveLeaveDays(leave, holidayDates)
    return sum + days
  }, 0)

  return Math.max(entitlementDays - usedDays, 0)
}

export const readHRMRecords = (key, fallbackFactory) => {
  const raw = localStorage.getItem(key)
  const parsed = safeParse(raw, null)
  if (Array.isArray(parsed)) return parsed
  if (parsed && typeof parsed === 'object' && Array.isArray(parsed.records)) return parsed.records
  return typeof fallbackFactory === 'function' ? fallbackFactory() : []
}

export const writeHRMRecords = (key, records) => {
  localStorage.setItem(key, JSON.stringify({ records: records.map(withCurrentTimestamp) }))
}

export const deleteHRMRecord = (key, recordId, fallbackFactory) => {
  const records = readHRMRecords(key, fallbackFactory).filter((item) => String(item.id) !== String(recordId))
  writeHRMRecords(key, records)
  return records
}

export const upsertHRMRecord = (key, record, fallbackFactory) => {
  const existing = readHRMRecords(key, fallbackFactory)
  const next = record.id ? existing.map((item) => (String(item.id) === String(record.id) ? { ...item, ...record } : item)) : [...existing, { ...record, id: String(Date.now()) }]
  writeHRMRecords(key, next)
  return next
}

export const getHRMDocStats = (documents = []) => {
  const today = new Date()
  const soonThreshold = new Date(today)
  soonThreshold.setDate(soonThreshold.getDate() + 90)

  const expiringSoon = documents.filter((doc) => {
    if (!doc?.expiryDate) return false
    const expiry = new Date(doc.expiryDate)
    return !Number.isNaN(expiry.getTime()) && expiry >= today && expiry <= soonThreshold
  }).length

  const missing = HRM_ONBOARDING_DOCUMENTS.filter((req) => !documents.find((doc) => doc.key === req.key && doc.fileName)).length

  return {
    total: documents.length,
    uploaded: documents.filter((doc) => doc.fileName).length,
    expiringSoon,
    missing,
  }
}
